import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ThemeVariable, VariableCategory } from './theme-variable.model';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly root = document.documentElement;
  private themeSubject = new BehaviorSubject<VariableCategory[]>([]);
  public theme$ = this.themeSubject.asObservable();

  constructor() {
    this.initializeThemeVariables();
  }

  private initializeThemeVariables() {
    const categories: VariableCategory[] = [
      {
        name: 'Typography',
        variables: [
          { name: '--font-family', value: this.getVariable('--font-family'), type: 'font', category: 'Typography' },
          { name: '--font-family-accent', value: this.getVariable('--font-family-accent'), type: 'font', category: 'Typography' }
        ]
      },
      {
        name: 'Basic Theme',
        variables: [
          { name: '--primary-color', value: this.getVariable('--primary-color'), type: 'color', category: 'Basic Theme' },
          { name: '--accent-color', value: this.getVariable('--accent-color'), type: 'color', category: 'Basic Theme' },
          { name: '--text-color', value: this.getVariable('--text-color'), type: 'color', category: 'Basic Theme' }
        ]
      },
      {
        name: 'Containers',
        variables: [
          { name: '--app-bg', value: this.getVariable('--app-bg'), type: 'color', category: 'Containers' },
          { name: '--container-bg', value: this.getVariable('--container-bg'), type: 'color', category: 'Containers' },
          { name: '--container-color', value: this.getVariable('--container-color'), type: 'color', category: 'Containers' },
          { name: '--container-reduced-color', value: this.getVariable('--container-reduced-color'), type: 'color', category: 'Containers' },
          { name: '--list-item-bg', value: this.getVariable('--list-item-bg'), type: 'color', category: 'Containers' },
          { name: '--list-item-color', value: this.getVariable('--list-item-color'), type: 'color', category: 'Containers' },
          { name: '--list-item-title-bg', value: this.getVariable('--list-item-title-bg'), type: 'color', category: 'Containers' },
          { name: '--list-item-title-color', value: this.getVariable('--list-item-title-color'), type: 'color', category: 'Containers' },
          { name: '--card-bg', value: this.getVariable('--card-bg'), type: 'color', category: 'Containers' },
          { name: '--card-hover-bg', value: this.getVariable('--card-hover-bg'), type: 'color', category: 'Containers' },
          { name: '--card-color', value: this.getVariable('--card-color'), type: 'color', category: 'Containers' }
        ]
      },
      {
        name: 'Buttons',
        variables: [
          { name: '--b-primary-bg', value: this.getVariable('--b-primary-bg'), type: 'color', category: 'Buttons' },
          { name: '--b-primary-color', value: this.getVariable('--b-primary-color'), type: 'color', category: 'Buttons' },
          { name: '--b-secondary-bg', value: this.getVariable('--b-secondary-bg'), type: 'color', category: 'Buttons' },
          { name: '--b-secondary-color', value: this.getVariable('--b-secondary-color'), type: 'color', category: 'Buttons' },
          { name: '--b-tertiary-bg', value: this.getVariable('--b-tertiary-bg'), type: 'color', category: 'Buttons' },
          { name: '--b-tertiary-color', value: this.getVariable('--b-tertiary-color'), type: 'color', category: 'Buttons' },
          { name: '--link-color', value: this.getVariable('--link-color'), type: 'color', category: 'Buttons' }
        ]
      },
      {
        name: 'Status Colors',
        variables: [
          { name: '--rollover-color', value: this.getVariable('--rollover-color'), type: 'color', category: 'Status Colors' },
          { name: '--bdg-color', value: this.getVariable('--bdg-color'), type: 'color', category: 'Status Colors' },
          { name: '--boosted-color', value: this.getVariable('--boosted-color'), type: 'color', category: 'Status Colors' },
          { name: '--reduced-color', value: this.getVariable('--reduced-color'), type: 'color', category: 'Status Colors' },
          { name: '--timer-zero', value: this.getVariable('--timer-zero'), type: 'color', category: 'Status Colors' },
          { name: '--timer-countdown', value: this.getVariable('--timer-countdown'), type: 'color', category: 'Status Colors' }
        ]
      },
      {
        name: 'Borders & Highlights',
        variables: [
          { name: '--highlight-bg', value: this.getVariable('--highlight-bg'), type: 'color', category: 'Borders & Highlights' },
          { name: '--highlight-color', value: this.getVariable('--highlight-color'), type: 'color', category: 'Borders & Highlights' },
          { name: '--global-border', value: this.getVariable('--global-border'), type: 'color', category: 'Borders & Highlights' },
          { name: '--global-border-reduced', value: this.getVariable('--global-border-reduced'), type: 'color', category: 'Borders & Highlights' }
        ]
      },
      {
        name: 'Tags & Stats',
        variables: [
          { name: '--stats', value: this.getVariable('--stats'), type: 'color', category: 'Tags & Stats' },
          { name: '--tag-bg', value: this.getVariable('--tag-bg'), type: 'color', category: 'Tags & Stats' },
          { name: '--tag-color', value: this.getVariable('--tag-color'), type: 'color', category: 'Tags & Stats' },
          { name: '--tag-border', value: this.getVariable('--tag-border'), type: 'color', category: 'Tags & Stats' }
        ]
      },
      {
        name: 'Status Indicators',
        variables: [
          { name: '--roi-bg', value: this.getVariable('--roi-bg'), type: 'color', category: 'Status Indicators' },
          { name: '--roi-color', value: this.getVariable('--roi-color'), type: 'color', category: 'Status Indicators' },
          { name: '--offer-bg', value: this.getVariable('--offer-bg'), type: 'color', category: 'Status Indicators' },
          { name: '--offer-color', value: this.getVariable('--offer-color'), type: 'color', category: 'Status Indicators' },
          { name: '--inplay-bg', value: this.getVariable('--inplay-bg'), type: 'color', category: 'Status Indicators' },
          { name: '--inplay-color', value: this.getVariable('--inplay-color'), type: 'color', category: 'Status Indicators' }
        ]
      },
      {
        name: 'Transaction States',
        variables: [
          { name: '--voided-bg', value: this.getVariable('--voided-bg'), type: 'color', category: 'Transaction States' },
          { name: '--voided-color', value: this.getVariable('--voided-color'), type: 'color', category: 'Transaction States' },
          { name: '--placed-bg', value: this.getVariable('--placed-bg'), type: 'color', category: 'Transaction States' },
          { name: '--placed-color', value: this.getVariable('--placed-color'), type: 'color', category: 'Transaction States' },
          { name: '--refunded-bg', value: this.getVariable('--refunded-bg'), type: 'color', category: 'Transaction States' },
          { name: '--refunded-color', value: this.getVariable('--refunded-color'), type: 'color', category: 'Transaction States' }
        ]
      },
      {
        name: 'Social Share',
        variables: [
          { name: '--share-wa-bg', value: this.getVariable('--share-wa-bg'), type: 'color', category: 'Social Share' },
          { name: '--share-wa-color', value: this.getVariable('--share-wa-color'), type: 'color', category: 'Social Share' },
          { name: '--share-tw-bg', value: this.getVariable('--share-tw-bg'), type: 'color', category: 'Social Share' },
          { name: '--share-tw-color', value: this.getVariable('--share-tw-color'), type: 'color', category: 'Social Share' },
          { name: '--share-fb-bg', value: this.getVariable('--share-fb-bg'), type: 'color', category: 'Social Share' },
          { name: '--share-fb-color', value: this.getVariable('--share-fb-color'), type: 'color', category: 'Social Share' }
        ]
      },
      {
        name: 'Notifications',
        variables: [
          { name: '--warning-bg', value: this.getVariable('--warning-bg'), type: 'color', category: 'Notifications' },
          { name: '--warning-color', value: this.getVariable('--warning-color'), type: 'color', category: 'Notifications' },
          { name: '--warning-border', value: this.getVariable('--warning-border'), type: 'color', category: 'Notifications' },
          { name: '--info-bg', value: this.getVariable('--info-bg'), type: 'color', category: 'Notifications' },
          { name: '--info-color', value: this.getVariable('--info-color'), type: 'color', category: 'Notifications' },
          { name: '--info-border', value: this.getVariable('--info-border'), type: 'color', category: 'Notifications' },
          { name: '--error-bg', value: this.getVariable('--error-bg'), type: 'color', category: 'Notifications' },
          { name: '--error-color', value: this.getVariable('--error-color'), type: 'color', category: 'Notifications' },
          { name: '--error-border', value: this.getVariable('--error-border'), type: 'color', category: 'Notifications' },
          { name: '--success-bg', value: this.getVariable('--success-bg'), type: 'color', category: 'Notifications' },
          { name: '--success-color', value: this.getVariable('--success-color'), type: 'color', category: 'Notifications' },
          { name: '--success-border', value: this.getVariable('--success-border'), type: 'color', category: 'Notifications' }
        ]
      },
      {
        name: 'Menu',
        variables: [
          { name: '--navbar', value: this.getVariable('--navbar'), type: 'color', category: 'Menu' },
          { name: '--v-menu-bg', value: this.getVariable('--v-menu-bg'), type: 'color', category: 'Menu' },
          { name: '--v-menu-border', value: this.getVariable('--v-menu-border'), type: 'color', category: 'Menu' },
          { name: '--v-menu-color', value: this.getVariable('--v-menu-color'), type: 'color', category: 'Menu' },
          { name: '--v-menu-active-bg', value: this.getVariable('--v-menu-active-bg'), type: 'color', category: 'Menu' },
          { name: '--v-menu-active-border', value: this.getVariable('--v-menu-active-border'), type: 'color', category: 'Menu' },
          { name: '--v-menu-active-color', value: this.getVariable('--v-menu-active-color'), type: 'color', category: 'Menu' },
          { name: '--v-menu-hover', value: this.getVariable('--v-menu-hover'), type: 'color', category: 'Menu' },
          { name: '--v-menu-hover-text', value: this.getVariable('--v-menu-hover-text'), type: 'color', category: 'Menu' }
        ]
      },
      {
        name: 'Submenu',
        variables: [
          { name: '--submenu-color', value: this.getVariable('--submenu-color'), type: 'color', category: 'Submenu' },
          { name: '--submenu-item-bg', value: this.getVariable('--submenu-item-bg'), type: 'color', category: 'Submenu' },
          { name: '--submenu-item-color', value: this.getVariable('--submenu-item-color'), type: 'color', category: 'Submenu' },
          { name: '--submenu-item-active-bg', value: this.getVariable('--submenu-item-active-bg'), type: 'color', category: 'Submenu' },
          { name: '--submenu-item-active-color', value: this.getVariable('--submenu-item-active-color'), type: 'color', category: 'Submenu' }
        ]
      },
      {
        name: 'Forms',
        variables: [
          { name: '--checkbox-bg', value: this.getVariable('--checkbox-bg'), type: 'color', category: 'Forms' },
          { name: '--checkbox-color', value: this.getVariable('--checkbox-color'), type: 'color', category: 'Forms' },
          { name: '--select-bg', value: this.getVariable('--select-bg'), type: 'color', category: 'Forms' },
          { name: '--select-label', value: this.getVariable('--select-label'), type: 'color', category: 'Forms' },
          { name: '--select-color', value: this.getVariable('--select-color'), type: 'color', category: 'Forms' },
          { name: '--input-bg', value: this.getVariable('--input-bg'), type: 'color', category: 'Forms' },
          { name: '--input-color', value: this.getVariable('--input-color'), type: 'color', category: 'Forms' },
          { name: '--input-addon-bg', value: this.getVariable('--input-addon-bg'), type: 'color', category: 'Forms' },
          { name: '--input-addon-color', value: this.getVariable('--input-addon-color'), type: 'color', category: 'Forms' }
        ]
      }
    ];

    this.themeSubject.next(categories);
  }

  private getVariable(name: string): string {
    return getComputedStyle(this.root).getPropertyValue(name).trim();
  }

  public updateVariable(variable: ThemeVariable) {
    this.root.style.setProperty(variable.name, variable.value);
    this.updateThemeState(variable);
  }

  private updateThemeState(updatedVariable: ThemeVariable) {
    const currentCategories = this.themeSubject.value;
    const updatedCategories = currentCategories.map(category => ({
      ...category,
      variables: category.variables.map(variable => 
        variable.name === updatedVariable.name ? updatedVariable : variable
      )
    }));
    this.themeSubject.next(updatedCategories);
  }

  public resetToDefault() {
    this.root.classList.remove('dark');
    this.root.classList.add('light');
    this.initializeThemeVariables();
  }

  public exportTheme(): string {
    const theme = {};
    this.themeSubject.value.forEach(category => {
      category.variables.forEach(variable => {
        theme[variable.name] = variable.value;
      });
    });
    return JSON.stringify(theme, null, 2);
  }

  public importTheme(themeJson: string) {
    try {
      const theme = JSON.parse(themeJson);
      Object.entries(theme).forEach(([name, value]) => {
        this.updateVariable({ name, value: value as string, type: 'color', category: 'Imported' });
      });
      this.initializeThemeVariables();
    } catch (error) {
      console.error('Failed to import theme:', error);
    }
  }
}